import { Badge, BadgeVariant } from "@tfg/ui"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"

import type {
  CertificateByOrganizationDto,
  CertificateByOrganizationDtoExtractionOutcomeEnumKey,
  CertificateByOrganizationDtoReviewReasonEnumKey,
} from "~/gen/api"

type CertificateExtractionOutcomeBadgeProps = {
  extractionOutcome: CertificateByOrganizationDto["extractionOutcome"]
  reviewReason: CertificateByOrganizationDto["reviewReason"]
}

const outcomeVariantByValue = {
  AUTO_ACCEPTED: BadgeVariant.SUCCESS,
  NEEDS_MANUAL_REVIEW: BadgeVariant.WARNING,
  EXTRACTION_FAILED: BadgeVariant.ERROR,
} as const satisfies Record<
  CertificateByOrganizationDtoExtractionOutcomeEnumKey,
  BadgeVariant
>

export function getExtractionOutcomeLabel(
  extractionOutcome: CertificateByOrganizationDtoExtractionOutcomeEnumKey,
  t: TFunction<"certificates">
): string {
  const labels = {
    AUTO_ACCEPTED: t("extractionOutcome.AUTO_ACCEPTED"),
    NEEDS_MANUAL_REVIEW: t("extractionOutcome.NEEDS_MANUAL_REVIEW"),
    EXTRACTION_FAILED: t("extractionOutcome.EXTRACTION_FAILED"),
  } as const satisfies Record<
    CertificateByOrganizationDtoExtractionOutcomeEnumKey,
    string
  >

  return labels[extractionOutcome]
}

export function getExtractionReviewReasonLabel(
  reviewReason: CertificateByOrganizationDtoReviewReasonEnumKey,
  t: TFunction<"certificates">
): string {
  const labels = {
    EXTRACTION_EMPTY: t("extractionOutcome.reviewReason.EXTRACTION_EMPTY"),
    LOW_QUALITY: t("extractionOutcome.reviewReason.LOW_QUALITY"),
    TYPE_MISMATCH: t("extractionOutcome.reviewReason.TYPE_MISMATCH"),
    UNSUPPORTED_TYPE: t("extractionOutcome.reviewReason.UNSUPPORTED_TYPE"),
  } as const satisfies Record<
    CertificateByOrganizationDtoReviewReasonEnumKey,
    string
  >

  return labels[reviewReason]
}

export const CertificateExtractionOutcomeBadge = ({
  extractionOutcome,
  reviewReason,
}: CertificateExtractionOutcomeBadgeProps) => {
  const { t } = useTranslation("certificates")

  if (extractionOutcome === null || extractionOutcome === undefined) {
    return null
  }

  const badgeText = getExtractionOutcomeLabel(extractionOutcome, t)
  const tooltipLabel =
    reviewReason !== null && reviewReason !== undefined
      ? getExtractionReviewReasonLabel(reviewReason, t)
      : badgeText
  const accessibilityLabel =
    reviewReason !== null && reviewReason !== undefined
      ? `${badgeText}. ${tooltipLabel}`
      : badgeText

  return (
    <Badge
      text={badgeText}
      variant={outcomeVariantByValue[extractionOutcome]}
      tooltipLabel={tooltipLabel}
      aria-label={accessibilityLabel}
    />
  )
}
